import React from 'react';

import registerLogger, { error as logError } from '@eika-infrastruktur/log';
import { runningInRNWebView } from '@eika-infrastruktur/mobilbank-lib';
import { WebToRNAction } from '@eika-infrastruktur/mobilbank-lib/src/nativeEvents';
import { postMessageRN } from '@eika-infrastruktur/mobilbank-lib/src/nativeHelper';
import { QueryClient, QueryClientProvider, QueryKey } from '@eika-infrastruktur/react-query';
import { loadContainers } from '@eika-infrastruktur/render';
import { SkeletonThemeProvider } from '@eika/skeleton';

import AutoHideWidget from './AutoHideWidget';
import { ErrorCatcher } from './components/ErrorCatcher';
import { HideWidgetOnFeatureToggle } from './components/HideWidgetOnFeatureToggle';
import { Config, defaultConfig, demoConfig, FeatureToggles } from './defaultConfig';
import { AVTALEGIRO_PROPOSAL_KEY } from './fetchHooks/useAvtalegiro';
import './polyfill';
import { CONTEXT_PATH } from './services';
import { logErrorOnQueryError } from './utils/logging/logError';
import { AboutApp } from './widgets/AboutApp';
import { AccountAdditionalLinks } from './widgets/AccountAdditionalLinks';
import { AccountDetails } from './widgets/AccountDetails';
import { AccountList as AccountListWidget } from './widgets/AccountList';
import { AccountOverview as AccountOverviewWidget } from './widgets/AccountOverview';
import { AccountView } from './widgets/AccountView';
import { AccountViewActions as AccountViewActionsApp } from './widgets/AccountViewActions';
import { AccountViewHeader as AccountViewHeaderApp } from './widgets/AccountViewHeader';
import { AdvisorCard } from './widgets/AdvisorCard';
import { AvtaleGiro } from './widgets/AvtaleGiro';
import { AvtaleGiroProposalLinkCard } from './widgets/AvtaleGiroProposalLinkCard';
import { AvtalegiroActions } from './widgets/AvtalegiroActions';
import BirthdayMessage from './widgets/BirthdayMessage/BirthdayMessage';
import { BusinessInformation } from './widgets/BusinessInformation';
import { ContactInformation } from './widgets/ContactInformation';
import CorporateHeaderApp from './widgets/CorporateHeaderApp/CorporateHeaderApp';
import { CreateAvtaleGiro } from './widgets/CreateAvtaleGiro';
import { CrossBorderPayment } from './widgets/CrossBorderPayment';
import { DemoView } from './widgets/DemoView';
import DemoDetails from './widgets/DemoView/DemoDetails';
import DemoLoanApplication from './widgets/DemoView/DemoLoanApplication';
import DemoPaymentPage from './widgets/DemoView/DemoPaymentPage';
import { EFakturaAgreement } from './widgets/EFakturaAgreement';
import { EFakturaHistory } from './widgets/EFakturaHistory';
import { EFakturaNotifications } from './widgets/EFakturaNotifications';
import { EFakturaReservations } from './widgets/EFakturaReservations';
import { EditAccountOverview } from './widgets/EditAccountOverview';
import { EditLoan } from './widgets/EditLoan';
import { EditSalaryInfo } from './widgets/EditSalaryInfo';
import { EditSalaryInfoStartPage } from './widgets/EditSalaryInfoStartPage';
import { EfakturaSharing } from './widgets/EfakturaSharing';
import { FavoriteAccount as FavoriteAccountWidget } from './widgets/FavoriteAccount';
import { ForceUpdateModal } from './widgets/ForceUpdateModal';
import { FuturePaymentsCard } from './widgets/FuturePaymentsCard';
import { FuturePaymentsEFaktura } from './widgets/FuturePaymentsEFaktura';
import { FuturePaymentsListPage } from './widgets/FuturePaymentsListPage';
import { ImmediatePayment } from './widgets/ImmediatePayment';
import { InvoiceArchive } from './widgets/InvoiceArchive';
import { KeepAlive } from './widgets/KeepAlive';
import { KredittbankenKyc } from './widgets/KredittbankenKYC';
import { LastLogin } from './widgets/LastLogin';
import { Loan } from './widgets/Loan';
import { LoanDetails } from './widgets/LoanDetails';
import { LoanDetailsOwners } from './widgets/LoanDetailsOwners';
import { LoanOverview } from './widgets/LoanOverview';
import { LoanRepaymentPlan } from './widgets/LoanRepaymentPlan';
import { LoanViewActions } from './widgets/LoanViewActions';
import { LoanViewHeader } from './widgets/LoanViewHeader';
import { Login } from './widgets/Login';
import { MoveAvtaleGiro } from './widgets/MoveAvtaleGiro';
import { MultipleApprovalEFakturaList } from './widgets/MultipleApprovalEFakturaList';
import { NotificationsSettings } from './widgets/NotificationsSettings';
import { OutboxApprovalList } from './widgets/OutboxApprovalList';
import { Payment } from './widgets/Payment';
import { PaymentActions } from './widgets/PaymentActions';
import { PaymentAndTransferPage } from './widgets/PaymentAndTransferPage';
import { PaymentSettings } from './widgets/PaymentSettings';
import PeriodicControl from './widgets/PeriodicControl/PeriodicControl';
import PopupManager from './widgets/PopupManager/PopupManager';
import { ProfileHeaderLink } from './widgets/ProfileHeaderLink';
import { PushPopup } from './widgets/PushPopup';
import { Receivers } from './widgets/Receivers';
import { RecurringPayments } from './widgets/RecurringPayments';
import { RecurringPaymentsActions } from './widgets/RecurringPaymentsActions';
import SalaryInfo from './widgets/SalaryInfo/SalaryInfo';
import SelectSigningAndLoginMethod from './widgets/SelectLoginAndSigningMethod/SelectSigningAndLoginMethod';
import { Settings } from './widgets/Settings';
import { StoppedPayments } from './widgets/StoppedPayments';
import { TaskManager } from './widgets/TaskManager';
import { ThirdPartyDependencies } from './widgets/ThirdPartyDependencies';
import { UpdatePaymentLimits } from './widgets/UpdatePaymentLimits';
import ChangeLanguage from './widgets/UserProfile/SwitchLanguage';
import UserProfile from './widgets/UserProfile/UserProfile';
import { VerticalEFakturaList } from './widgets/VerticalEFakturaList';

import './styles/main.scss';

const NAMESPACE = 'dagligbank-lokalbank-web';
const restBaseUrl = `${CONTEXT_PATH}/rest/resource/log`;
registerLogger(restBaseUrl, NAMESPACE);

// eslint-disable-next-line import/prefer-default-export
export const globalCssNamespace = 'elb no-eika-lokalbank';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            throwOnError: logErrorOnQueryError,
            staleTime: Infinity,
            retry: 1,
            refetchOnWindowFocus: false,
            refetchIntervalInBackground: false,
            retryOnMount: false,
        },
    },
});

type RenderAppOptions = {
    // @default false;
    themed?: boolean;
    // @default [] meanining no queries will be invalidated
    suspenseQueriesToInvalidateOnError?: QueryKey[];
    // @default undefined
    targetFeatureToggle?: keyof FeatureToggles;
    // @default true
    showErrorCardOnBackendError?: boolean;
    // @default true
    showErrorCard?: boolean;
    // @default defaultConfig
    config?: Config;
};

const baseNamespace = 'dagligbank.lokalbank.';
const renderApp =
    (MyApp: React.ComponentType, options?: RenderAppOptions) =>
    (namespace: string): void => {
        const actualOptions: RenderAppOptions = {
            themed: false,
            suspenseQueriesToInvalidateOnError: [],
            config: defaultConfig,
            targetFeatureToggle: undefined,
            showErrorCardOnBackendError: true,
            showErrorCard: true,
            ...options,
        };
        loadContainers(
            `${baseNamespace}${namespace}`,
            () => (
                <React.StrictMode>
                    <QueryClientProvider client={queryClient}>
                        <ErrorCatcher
                            appNamespace={namespace}
                            noBackgroundOnErrorCard={actualOptions.themed}
                            queriesToInvalidateOnRetry={actualOptions.suspenseQueriesToInvalidateOnError}
                            showErrorCardOnBackendError={actualOptions.showErrorCardOnBackendError}
                            showErrorCard={actualOptions.showErrorCard}
                        >
                            <HideWidgetOnFeatureToggle featureToggleKey={actualOptions.targetFeatureToggle}>
                                <AutoHideWidget globalCssNamespace={globalCssNamespace}>
                                    <SkeletonThemeProvider>
                                        <MyApp />
                                    </SkeletonThemeProvider>
                                </AutoHideWidget>
                            </HideWidgetOnFeatureToggle>
                        </ErrorCatcher>
                    </QueryClientProvider>
                </React.StrictMode>
            ),
            actualOptions.config,
        );
    };

const apps: Record<string, (namespace: string) => void> = {
    favoriteaccount: renderApp(FavoriteAccountWidget, { themed: true }),
    overview: renderApp(AccountOverviewWidget),
    'account-list': renderApp(AccountListWidget),
    'edit-account-overview': renderApp(EditAccountOverview),
    'account-view': renderApp(AccountView),
    'vertical-efaktura-list': renderApp(VerticalEFakturaList), // TODO: cleanup in another task
    'futurepayments-efaktura': renderApp(FuturePaymentsEFaktura),
    futurepaymentsactionheader: renderApp(PaymentActions),
    'payment-actions': renderApp(PaymentActions),
    'loan-overview': renderApp(LoanOverview),
    'loan-view': renderApp(Loan),
    'loan-details': renderApp(LoanDetails),
    'loan-details-owners': renderApp(LoanDetailsOwners),
    'demo-view': renderApp(DemoView, { config: demoConfig }),
    'demo-details-view': renderApp(DemoDetails, { config: demoConfig }),
    'demo-payment-page': renderApp(DemoPaymentPage, { config: demoConfig }),
    'demo-loan-application': renderApp(DemoLoanApplication, { config: demoConfig }),
    'account-details-view': renderApp(AccountDetails),
    'recurring-payments': renderApp(RecurringPayments),
    'recurring-payments-actions': renderApp(RecurringPaymentsActions),
    receivers: renderApp(Receivers),
    payment: renderApp(Payment), // TODO: This app crashes and is not in use in EikaView
    'stopped-payments': renderApp(StoppedPayments),
    avtalegiro: renderApp(AvtaleGiro),
    'avtalegiro-actions': renderApp(AvtalegiroActions),
    advisor: renderApp(AdvisorCard),
    profile: renderApp(UserProfile),
    'efaktura-agreement': renderApp(EFakturaAgreement),
    'efaktura-history': renderApp(EFakturaHistory),
    'efaktura-reservations': renderApp(EFakturaReservations),
    'efaktura-notifications': renderApp(EFakturaNotifications),
    settings: renderApp(Settings),
    login: renderApp(Login),
    'payment-settings': renderApp(PaymentSettings),
    'about-app': renderApp(AboutApp),
    'third-party-dependencies': renderApp(ThirdPartyDependencies),
    'create-avtalegiro': renderApp(CreateAvtaleGiro),
    'invoice-archive': renderApp(InvoiceArchive),
    'force-update-modal': renderApp(ForceUpdateModal),
    'immediate-payment': renderApp(ImmediatePayment),
    'cross-border-payment': renderApp(CrossBorderPayment),
    'choose-login-signing': renderApp(SelectSigningAndLoginMethod),
    'profile-header-link': renderApp(ProfileHeaderLink, { showErrorCard: false }),
    'account-view-actions': renderApp(AccountViewActionsApp),
    'account-view-links': renderApp(AccountAdditionalLinks),
    'account-view-header': renderApp(AccountViewHeaderApp),
    'keep-alive': renderApp(KeepAlive),
    'contact-information': renderApp(ContactInformation),
    'periodic-control': renderApp(PeriodicControl),
    'corperate-header-app': renderApp(CorporateHeaderApp, { showErrorCard: false }),
    'outbox-approval-list': renderApp(OutboxApprovalList),
    'payment-and-transfer-page': renderApp(PaymentAndTransferPage),
    'payment-page': renderApp(() => <PaymentAndTransferPage noTabs />),
    'update-payment-limits': renderApp(UpdatePaymentLimits),
    futurepaymentslist: renderApp(FuturePaymentsListPage),
    'futurepaymentslist-with-title': renderApp(() => <FuturePaymentsListPage withTitle />),
    'futurepayments-card': renderApp(FuturePaymentsCard),
    'loan-repayment-plan': renderApp(LoanRepaymentPlan),
    'edit-loan': renderApp(EditLoan),
    'edit-salary-info': renderApp(EditSalaryInfo),
    'salary-info': renderApp(SalaryInfo),
    'edit-salary-info-start-page': renderApp(EditSalaryInfoStartPage),
    'change-language': renderApp(ChangeLanguage),
    'loan-actions': renderApp(LoanViewActions),
    'loan-view-header': renderApp(LoanViewHeader),
    'notifications-settings': renderApp(NotificationsSettings),
    'efaktura-sharing': renderApp(EfakturaSharing),
    'task-manager': renderApp(TaskManager),
    'last-login': renderApp(LastLogin),
    'business-information': renderApp(BusinessInformation),
    'push-popup': renderApp(PushPopup),
    'move-avtalegiro': renderApp(MoveAvtaleGiro),
    'birthday-message': renderApp(BirthdayMessage),
    'multiple-approve-efaktura-list': renderApp(MultipleApprovalEFakturaList),
    'avtalegiro-proposal-link-card': renderApp(AvtaleGiroProposalLinkCard, {
        suspenseQueriesToInvalidateOnError: [AVTALEGIRO_PROPOSAL_KEY],
        targetFeatureToggle: 'enableAvtaleGiroProposals',
        showErrorCardOnBackendError: false,
    }),
    'kredittbanken-kyc': renderApp(KredittbankenKyc),
    'popup-manager': renderApp(PopupManager),
};

Object.keys(apps).forEach((key: string) => apps[key](key));

type RerenderParams = {
    namespace: string;
};

window.addEventListener('engine:rerender', (event: Event) => {
    try {
        const customEvent = event as CustomEvent<RerenderParams>;
        if (customEvent?.detail?.namespace && customEvent.detail.namespace.startsWith(baseNamespace)) {
            const simpleNamespace = customEvent.detail.namespace.replace(baseNamespace, '');
            apps[simpleNamespace](simpleNamespace);
        }
    } catch (e) {
        logError(e);
    }
});

if (runningInRNWebView) {
    postMessageRN({ action: WebToRNAction.WEBAPP_LOADED, payload: undefined });
}
